import { FieldTypes } from '../../constants'
import { CheckboxField, DropdownField, Field, FieldMap } from '../../types/forms/fields'
import { addCondition } from '../utils'
import { getYesNoUnknownField } from './shared'

const getFamilyAndGeneticHistoryFields = (
  id: string,
  placeholder: string,
): Record<string, Field> => {
  const checkboxField = addCondition(getYesNoUnknownField(placeholder), v => !v?.noneOfTheBelow)

  return {
    [id]: checkboxField,
    [`${id}FamilyMember`]: {
      type: FieldTypes.TEXT,
      placeholder: 'Family member(s) with condition',
      hints: [
        {
          text: 'e.g. mother, father, sister, brother, etc.',
        },
      ],
      condition: v => v?.[id] === 'yes' && !v?.noneOfTheBelow,
    },
    [`${id}Details`]: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Please provide any details about the condition',
      condition: v => v?.[id] === 'yes' && !v?.noneOfTheBelow,
      optional: true,
    },
  }
}

const getFamilyAndGeneticHistoryOtherFields = (
  id: string,
  placeholder: string,
): Record<string, Field> => {
  const checkboxField: DropdownField = {
    type: FieldTypes.DROPDOWN,
    placeholder,
    inputType: 'radio',
    condition: v => !v?.noneOfTheBelow,
    options: [
      {
        text: 'No',
        id: 'no',
      },
      {
        text: 'Unknown',
        id: 'unknown',
      },
      {
        text: 'Yes',
        id: 'yes',
      },
    ],
  }

  return {
    [id]: checkboxField,
    [`${id}Condition`]: {
      type: FieldTypes.TEXT,
      placeholder: 'Other condition or disease',
      condition: v => v?.[id] === 'yes' && !v?.noneOfTheBelow,
    },
    [`${id}FamilyMember`]: {
      type: FieldTypes.TEXT,
      placeholder: 'Family member(s) with condition',
      hints: [
        {
          text: 'e.g. mother, father, sister, brother, etc.',
        },
      ],
      condition: v => v?.[id] === 'yes' && !v?.noneOfTheBelow,
    },
  }
}

const noneOfTheBelowField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'None of the below',
  inputType: 'switch',
  size: 'md',
}

export const familyHistoryField: FieldMap = {
  name: 'Family History',
  children: {
    noneOfTheBelow: noneOfTheBelowField,
    ...getFamilyAndGeneticHistoryFields('heartDisease', 'Heart disease'),
    ...getFamilyAndGeneticHistoryFields('highBloodPressure', 'High blood pressure'),
    ...getFamilyAndGeneticHistoryFields('stroke', 'Stroke'),
    ...getFamilyAndGeneticHistoryFields('lungDisease', 'Lung disease'),
    ...getFamilyAndGeneticHistoryFields('cancer', 'Cancer'),
    ...getFamilyAndGeneticHistoryFields('glaucoma', 'Glaucoma'),
    ...getFamilyAndGeneticHistoryFields('diabetes', 'Diabetes'),
    ...getFamilyAndGeneticHistoryFields('epilepsy', 'Epilepsy / Seizures'),
    ...getFamilyAndGeneticHistoryFields('thyroidDisease', 'Thyroid disease'),
    ...getFamilyAndGeneticHistoryFields('giDisease', 'GI disease'),
    ...getFamilyAndGeneticHistoryFields('liverDisease', 'Liver disease'),
    ...getFamilyAndGeneticHistoryFields('kidneyDisease', 'Kidney disease'),
    ...getFamilyAndGeneticHistoryFields('osteoporosis', 'Osteoporosis'),
    ...getFamilyAndGeneticHistoryFields('clottingDisorder', 'Clotting disorder'),
    ...getFamilyAndGeneticHistoryFields('depression', 'Depression'),
    ...getFamilyAndGeneticHistoryFields('mentalIllness', 'Mental illness'),
    ...getFamilyAndGeneticHistoryFields('tongueTie', 'Tongue tie'),
    ...getFamilyAndGeneticHistoryOtherFields('other', 'Other condition or disease'),
  },
}

const geneticHistoryField: FieldMap = {
  name: 'Genetic History',
  children: {
    noneOfTheBelow: noneOfTheBelowField,
    ...getFamilyAndGeneticHistoryFields('twins', 'Has any family member had twins?'),
    ...getFamilyAndGeneticHistoryFields('congenitalHeartDisease', 'Congenital heart disease'),
    ...getFamilyAndGeneticHistoryFields('spinaBifida', 'Neural tube defect (spina bifida)'),
    ...getFamilyAndGeneticHistoryFields('downSyndrome', 'Down syndrome'),
    ...getFamilyAndGeneticHistoryFields('taySachsDisease', 'Tay-Sachs disease'),
    ...getFamilyAndGeneticHistoryFields('canavanDisease', 'Canavan disease'),
    ...getFamilyAndGeneticHistoryFields('autism', 'Autism spectrum disorder'),
    ...getFamilyAndGeneticHistoryFields('developmentalDelay', 'Developmental delay or disability'),
    ...getFamilyAndGeneticHistoryFields('hemophilia', 'Hemophilia'),
    ...getFamilyAndGeneticHistoryFields('thalassemia', 'Thalassemia'),
    ...getFamilyAndGeneticHistoryFields('sickleCellAnemia', 'Sickle cell disease or trait'),
    ...getFamilyAndGeneticHistoryFields('muscularDystrophy', 'Muscular dystrophy'),
    ...getFamilyAndGeneticHistoryFields('cysticFibrosis', 'Cystic fibrosis'),
    ...getFamilyAndGeneticHistoryFields('huntingtonsChorea', "Huntington's chorea"),
    ...getFamilyAndGeneticHistoryFields('metabolicDisorder', 'Metabolic disorder'),
    ...getFamilyAndGeneticHistoryOtherFields('otherBirthDefect', 'Other birth defect'),
    ...getFamilyAndGeneticHistoryOtherFields('other', 'Other'),
  },
}

export const familyAndGeneticHistoryField: FieldMap = {
  name: 'Family and Genetic',
  labels: [
    {
      text: 'For each applicable condition, please list the relationship of the family member to you (e.g. mother, father, sister, brother, etc.)',
    },
  ],
  children: {
    family: familyHistoryField,
    genetic: geneticHistoryField,
  },
}
